import React, { useContext, useState } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  TextField,
  Backdrop,
  CircularProgress,
  Tooltip,
  Grid,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { doc, getDoc, updateDoc, getDocs, collectionGroup, setDoc } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { AppContext } from '../AppContext';
import { db, storage, auth, functionsClient } from '../firebaseConfig';
import { trackFeature } from '../utils/analytics';
import { deleteObject, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ConfirmationDialog from '../components/ConfirmationDialog';
import UserAvatar from '../components/UserAvatar';
import AvatarCropDialog from '../components/AvatarCropDialog';
import { SnackbarContext } from '../SnackbarContext';
import { cropImageToSquare } from '../utils/imageProcessing';
// UserInfo component (unchanged)
function UserInfo({ user, userData, showPasswordResetDialog }) {
  // Local states for editing Display Name
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState(userData?.displayName || '');
  const [avatarCropSource, setAvatarCropSource] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const { showSuccess, showError } = useContext(SnackbarContext);

  const getMemberFarmIdsForUser = async (uid) => {
    if (!uid) return [];
    try {
      const membersSnap = await getDocs(collectionGroup(db, 'members'));
      return Array.from(
        new Set(
          membersSnap.docs
            .filter((memberSnap) => memberSnap.id === uid)
            .map((memberSnap) => memberSnap.ref.parent.parent?.id)
            .filter(Boolean)
        )
      );
    } catch {
      return [];
    }
  };

  const syncMemberProfileAcrossFarms = async (uid, patch = {}) => {
    if (!uid) return;
    const farmIds = await getMemberFarmIdsForUser(uid);
    if (farmIds.length === 0) return;

    await Promise.allSettled(
      farmIds.map((farmId) =>
        setDoc(doc(db, 'farms', farmId, 'members', uid), patch, { merge: true })
      )
    );
  };

  if (!userData) {
    return <Typography>No user data available.</Typography>;
  }

  // Save new display name to Firestore
  const handleSaveName = async () => {
    try {
      const trimmedName = tempName.trim();

      // 1) Check if empty
      if (!trimmedName) {
        showError('Display name cannot be empty.');
        return;
      }

      // 2) Check max length (e.g., 20 characters)
      if (trimmedName.length > 20) {
        showError('Display name cannot exceed 20 characters.');
        return;
      }

      await updateDoc(doc(db, 'users', userData.uid), {
        displayName: trimmedName,
      });

      await syncMemberProfileAcrossFarms(userData.uid, { displayName: trimmedName });

      showSuccess('Display name updated successfully.');
      trackFeature('display_name_updated');
      setEditingName(false);
    } catch (err) {
      showError('Failed to update display name.');
    }
  };

  const handleEnterSave = (event, saveFn) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveFn();
    }
  };

  // ---- Avatar Upload Functionality ----
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !userData?.uid) return;
    e.target.value = '';

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showError('Please upload a PNG, JPG, or WEBP image.');
      return;
    }

    const maxOriginalSizeInBytes = 20 * 1024 * 1024;
    if (file.size > maxOriginalSizeInBytes) {
      showError('Original image size cannot exceed 20MB.');
      return;
    }

    if (avatarCropSource) {
      URL.revokeObjectURL(avatarCropSource);
    }
    setAvatarCropSource(URL.createObjectURL(file));
  };

  const handleCancelAvatarCrop = () => {
    if (uploadingAvatar) return;
    if (avatarCropSource) {
      URL.revokeObjectURL(avatarCropSource);
    }
    setAvatarCropSource('');
  };

  const handleConfirmAvatarCrop = async (cropPixels) => {
    if (!avatarCropSource || !userData?.uid) return;

    setUploadingAvatar(true);
    try {
      const resizedBlob = await cropImageToSquare(avatarCropSource, cropPixels, 256);
      const maxUploadedSizeInBytes = 2 * 1024 * 1024;
      if (resizedBlob.size > maxUploadedSizeInBytes) {
        showError('The resized avatar is still larger than 2MB. Please choose a simpler image.');
        return;
      }

      const avatarRef = ref(storage, `avatars/${userData.uid}/avatar.png`);

      const snapshot = await uploadBytes(avatarRef, resizedBlob, {
        contentType: resizedBlob.type,
      });
      const downloadURL = await getDownloadURL(snapshot.ref);

      await updateDoc(doc(db, 'users', userData.uid), {
        photoURL: downloadURL,
      });
      await syncMemberProfileAcrossFarms(userData.uid, { photoURL: downloadURL });

      showSuccess('Avatar uploaded successfully.');
      trackFeature('avatar_uploaded');
      URL.revokeObjectURL(avatarCropSource);
      setAvatarCropSource('');
    } catch (error) {
      console.error('Avatar upload failed:', error);
      showError(`Failed to upload avatar: ${error?.message || 'Unknown error'}`);
    } finally {
      setUploadingAvatar(false);
    }
  };

  // ---- Delete Avatar Functionality ----
  const handleDeleteAvatar = async () => {
    if (!userData?.uid) return;
    try {
      const avatarRef = ref(storage, `avatars/${userData.uid}/avatar.png`);
      await deleteObject(avatarRef);
      await updateDoc(doc(db, 'users', userData.uid), {
        photoURL: null,
      });
      await syncMemberProfileAcrossFarms(userData.uid, { photoURL: '' });
      showSuccess('Avatar removed successfully.');
      trackFeature('avatar_removed');
    } catch (error) {
      showError('Failed to remove avatar.');
    }
  };

  return (
    <Table
      size="small"
      sx={{
        '& .MuiTableCell-root': {
          p: { xs: 1, sm: 1.25 },
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <TableBody>
        {/* Display Name Row */}
        <TableRow>
          <TableCell width="30%">Display Name</TableCell>
          <TableCell>
            {!editingName ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 144px', alignItems: 'center', gap: 1 }}>
                <Typography>{userData.displayName || 'N/A'}</Typography>
                <Box sx={{ width: 144, display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setTempName(userData.displayName || '');
                      setEditingName(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 144px' }, alignItems: 'center', gap: 1 }}>
                <Box>
                  <TextField
                    size="small"
                    value={tempName}
                    onChange={(e) => setTempName(e.target.value)}
                    onKeyDown={(e) => handleEnterSave(e, handleSaveName)}
                    fullWidth
                  />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 144 }, display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  <IconButton
                    aria-label="Save display name"
                    color="success"
                    size="small"
                    onClick={handleSaveName}
                  >
                    <CheckIcon />
                  </IconButton>
                  <IconButton
                    aria-label="Cancel display name edit"
                    color="error"
                    size="small"
                    onClick={() => setEditingName(false)}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

            )}
          </TableCell>
        </TableRow>
        {/* Email Row */}
        <TableRow>
          <TableCell width="30%">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>Account Email</span>
              <Tooltip
                title={
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    This is the email you signed up with, this email will be used for any correspondance.
                  </Typography>
                }>
                <HelpOutlineIcon fontSize="small" sx={{ cursor: 'pointer' }} />
              </Tooltip>
            </Box>
          </TableCell>
          <TableCell width="70%">
            <Typography>{userData.email || 'N/A'}</Typography>
          </TableCell>
        </TableRow>

        {/* Reset Password Row - Only if signed in with email/pass */}
        {auth?.currentUser?.providerData[0]?.providerId === 'password' && (
          <TableRow>
            <TableCell width="30%">Reset Password</TableCell>
            <TableCell width="70%">
              <Button
                variant="outlined"
                color="warning"
                onClick={showPasswordResetDialog}
              >
                Reset Password
              </Button>
            </TableCell>
          </TableRow>
        )}


        {/* Avatar Row */}
        <TableRow>
          <TableCell width="30%">Profile Picture</TableCell>
          <TableCell width="70%">
            <input
              type="file"
              accept="image/png, image/jpeg, image/webp"
              id="avatar-upload"
              onChange={handleAvatarUpload}
              style={{ display: 'none' }}
            />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <label htmlFor="avatar-upload">
                  <Button variant="contained" color="secondary" component="span">
                    {userData.photoURL ? 'Change Avatar' : 'Upload Avatar'}
                  </Button>
                </label>
                {userData.photoURL && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleDeleteAvatar}
                  >
                    <DeleteIcon />
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <UserAvatar user={user} userData={userData} size={96} />
              </Box>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Allowed file types: PNG, JPG, WEBP. Original image up to 20MB.
            </Typography>
            <AvatarCropDialog
              open={Boolean(avatarCropSource)}
              imageSrc={avatarCropSource}
              saving={uploadingAvatar}
              onCancel={handleCancelAvatarCrop}
              onConfirm={handleConfirmAvatarCrop}
            />
          </TableCell>
        </TableRow>

      </TableBody>
    </Table>
  );
}

export default function AccountPage({ userData: routeUserData }) {
  const { userData: contextUserData, user } = useContext(AppContext);
  const userData = routeUserData || contextUserData;

  let createdAtString = 'N/A';
  if (userData?.createdAt?.toDate) {
    createdAtString = userData.createdAt.toDate().toLocaleString();
  }
  let expiresString = 'N/A';
  if (userData?.accessExpires?.toDate) {
    const expiresDate = userData.accessExpires.toDate();
    expiresString = expiresDate > new Date() ? expiresDate.toLocaleString() : 'No Active Subscription';
  }

  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  const [redeemCode, setRedeemCode] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');
  const [startingTrial, setStartingTrial] = useState(false);

  const { showSuccess, showError } = useContext(SnackbarContext);

  // Confirmation Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmRequiredWord, setConfirmRequiredWord] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleSendPasswordReset = async () => {
    try {
      if (!user?.email) {
        showError('No account email found.');
        return;
      }
      await sendPasswordResetEmail(auth, user.email);
      showSuccess(`Password reset email sent to ${user.email}`);
    } catch (err) {
      showError('Failed to send password reset email.');
    } finally {
      setResetPasswordDialogOpen(false);
    }
  };


  // Open Confirmation Dialog with title, message, action, and (optional) required word
  const openConfirmDialog = (title, message, action, requiredWord = null) => {
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setConfirmRequiredWord(requiredWord);
    setConfirmOpen(true);
  };

  // Execute confirmed action
  const handleConfirm = async () => {
    setConfirmOpen(false); // close dialog first
    if (confirmAction) {
      await confirmAction();
    }
  };


  // ---- Redeem Code Logic ----
  const handleRedeem = async () => {
    setRedeemMessage('');
    const code = redeemCode.trim();
    if (!code) {
      showError('Please enter a code.');
      return;
    }
    try {
      const codeRef = doc(db, 'accessCodes', code);
      const codeSnap = await getDoc(codeRef);
      if (!codeSnap.exists()) {
        showError(`Code "${code}" does not exist.`);
        return;
      }
      const codeData = codeSnap.data();
      if (codeData.redeemed) {
        showError(`Code "${code}" is already redeemed.`);
        return;
      }
      // Mark the code as redeemed
      await updateDoc(codeRef, {
        redeemed: true,
        redeemedAt: new Date(),
      });
      // Extend user's subscription if accessDuration exists
      if (userData?.uid && codeData.accessDuration) {
        const userRef = doc(db, 'users', userData.uid);
        const oldExpires = userData.accessExpires?.toDate ? userData.accessExpires.toDate() : new Date();
        const now = new Date();
        const baseDate = now > oldExpires ? now : oldExpires;
        baseDate.setDate(baseDate.getDate() + codeData.accessDuration);
        await updateDoc(userRef, {
          accessExpires: baseDate,
          approved: true,
          trialUsed: true,
        });
      } else if (userData?.uid) {
        const userRef = doc(db, 'users', userData.uid);
        await updateDoc(userRef, {
          approved: true,
          trialUsed: true,
        });
      }
      setRedeemCode('');
      showSuccess(`Success! Code "${code}" has been redeemed.`);
    } catch (err) {
      showError('Failed to redeem code.');
    }
  };

  const handleStartTrial = async () => {
    if (!userData?.uid) {
      showError('User account not found.');
      return;
    }
    if (userData.trialUsed) {
      showError('This account has already used its free trial.');
      return;
    }

    setStartingTrial(true);
    try {
      const now = new Date();
      const existingExpires = userData.accessExpires?.toDate
        ? userData.accessExpires.toDate()
        : now;
      const trialExpires = existingExpires > now
        ? new Date(existingExpires)
        : new Date(now);
      trialExpires.setDate(trialExpires.getDate() + 7);

      await updateDoc(doc(db, 'users', userData.uid), {
        approved: true,
        accessExpires: trialExpires,
        trialUsed: true,
      });

      showSuccess(`Your 7-day trial is active until ${trialExpires.toLocaleString()}.`);
    } catch (err) {
      showError('Failed to start the free trial.');
    } finally {
      setStartingTrial(false);
    }
  };

  // ------------------------------------------------
  // DANGER ZONE: Deleting Account
  // ------------------------------------------------

  // 4) Delete the entire account (and all user data)
  const handleDeleteAccount = async () => {
    if (!userData?.uid || !user) {
      showError('User not logged in.');
      return;
    }
    openConfirmDialog(
      'Delete Entire Account',
      'Are you sure you want to permanently delete your entire account and all associated data? This cannot be undone! Type "MICROGREEN" below to confirm.',
      async () => {
        setDeleting(true); // show backdrop

        try {
          const deleteMyAccount = httpsCallable(functionsClient, 'deleteMyAccount');
          await deleteMyAccount({ confirmation: 'MICROGREEN' });

          showSuccess('Account and owned farm data deleted.');
          window.location.href = '/';
        } catch (error) {
          console.error('Account deletion failed:', error);
          showError(
            error?.message
            || 'Failed to delete account. Please try again or contact support.'
          );
        } finally {
          setDeleting(false);
        }
      },
      'MICROGREEN'
    );
  };


  return (
    <Box
      sx={{
        maxWidth: '80%',
        py: '3',
        backgroundColor: (theme) => theme.palette.background.default,
        color: (theme) => theme.palette.text.primary,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '15px',
        '@media (max-width: 600px)': { maxWidth: '100%' },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: (theme) => theme.palette.background.default,
          zIndex: -1,
        },
      }}
    >
      <Typography variant="h4" gutterBottom>
        Account Page
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box>
          <TableContainer component={Paper}>
            <UserInfo
              user={user}
              userData={userData}
              showPasswordResetDialog={() => setResetPasswordDialogOpen(true)}
            />
          </TableContainer>
        </Box>

        <Box>
          <TableContainer component={Paper} sx={{ mb: 2 }}>
            <Table
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiTableCell-root': { border: '1px solid', borderColor: 'divider', py: 1.5 },
                '& .MuiTableCell-body:first-of-type': { fontWeight: 'bold' },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell width="45%">Account Created</TableCell>
                  <TableCell width="55%">{createdAtString}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell width="45%">Subscribed Until</TableCell>
                  <TableCell width="55%">{expiresString}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <TableContainer component={Paper}>
            <Table
              size="small"
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                '& .MuiTableCell-root': { border: '1px solid', borderColor: 'divider', py: 2.5 },
                '& .MuiTableCell-head, & .MuiTableCell-body:first-of-type': { fontWeight: 'bold' },
              }}
            >
              <TableBody>
                <TableRow
                  sx={{
                    display: { xs: 'block', sm: 'table-row' },
                  }}
                >
                  <TableCell
                    sx={{
                      width: { sm: '80%' },
                      display: { xs: 'block', sm: 'table-cell' },
                      borderBottom: { xs: 'none', sm: '1px solid' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: 1.5,
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: { sm: 120 },
                          fontWeight: 'bold',
                        }}
                      >
                        Enter Timecode
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={redeemCode}
                        onChange={(e) => setRedeemCode(e.target.value)}
                        placeholder="e.g. ABC123"
                      />
                    </Box>
                  </TableCell>

                  <TableCell
                    sx={{
                      width: { sm: '20%' },
                      display: { xs: 'block', sm: 'table-cell' },
                      borderTop: { xs: 'none', sm: '1px solid' },
                      p: { xs: 1.5, sm: 1 },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleRedeem}
                        fullWidth={true}
                        sx={{
                          ml: { xs: 0, sm: 1 },
                          mr: { xs: 0, sm: 1 },
                          px: 2,
                          borderRadius: 1,
                        }}
                      >
                        Redeem
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
                {!userData?.trialUsed && (
                  <TableRow
                    sx={{
                      display: { xs: 'block', sm: 'table-row' },
                    }}
                  >
                    <TableCell
                      sx={{
                        width: { sm: '80%' },
                        display: { xs: 'block', sm: 'table-cell' },
                        borderBottom: { xs: 'none', sm: '1px solid' },
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 'bold' }}>
                          7-Day Free Trial
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Activate one free 7-day trial for this account.
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell
                      sx={{
                        width: { sm: '20%' },
                        display: { xs: 'block', sm: 'table-cell' },
                        borderTop: { xs: 'none', sm: '1px solid' },
                        p: { xs: 1.5, sm: 1 },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: { xs: 'center', sm: 'flex-end' },
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={handleStartTrial}
                          disabled={startingTrial}
                          fullWidth={true}
                          sx={{
                            ml: { xs: 0, sm: 1 },
                            mr: { xs: 0, sm: 1 },
                            px: 2,
                            borderRadius: 1,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {startingTrial ? 'Starting...' : 'Start Trial'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h5" gutterBottom color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>Danger Zone</span>
              <WarningAmberIcon sx={{ color: 'error.main' }} />
            </Typography>

            <Paper
              sx={{
                px: 2,
                py: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: '4px solid',
                borderLeftColor: 'error.main',
                boxShadow: 'none',
                backgroundColor: 'transparent',
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Delete account
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Permanently delete your account, farm, and all farm data. This cannot be reversed.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="error"
                    size="large"
                    onClick={handleDeleteAccount}
                    sx={{ minWidth: 220 }}
                  >
                    Delete Account
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        </Box>
      </Box>


      <ConfirmationDialog
        open={resetPasswordDialogOpen}
        title="Reset Password"
        message={`A password reset email will be sent to: ${user?.email}. Continue?`}
        onConfirm={handleSendPasswordReset}
        onClose={() => setResetPasswordDialogOpen(false)}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
        requiredWord={confirmRequiredWord}
      />

      <Backdrop
        open={deleting}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999 }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Deleting account...
        </Typography>
      </Backdrop>

    </Box>
  );
}









